import StudioBrand from './StudioBrand';
import { useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, Save, ShieldCheck, RefreshCw } from 'lucide-react';
import { loadAreaRows } from './TranslationStudio';
import sourceManifest from '../../../api/_shared/clinicalSourceManifest.json';
import clinicalOnlyRows from '../data/clinicalOnlySourceRows.generated.json';

const AREAS = [
  ['hospital_journeys', 'National Journeys, safety and print'],
  ['what_you_might_hear', 'Clinical terminology'],
  ['know_your_team', 'Roles and patient guides'],
  ['core_ui', 'Patient UI and preparation'],
  ['know_your_hospital', 'Hospital information and signposting'],
];
const DECISIONS = [
  ['clinically_accurate','Clinical wording accurate'],
  ['changes_requested','Clinical change required'],
  ['needs_context','More context required'],
  ['nonclinical','Not clinically significant'],
];
const SIZE = 25;
async function request(method, query = '', body) {
  const res = await fetch('/api/studio-clinical-sources' + query, {
    method, credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || 'National clinical review could not be loaded');
  return payload;
}
export default function NationalClinicalSourceReviewStudio({ workspaceSwitcher = null, studioAccess = null }) {
  const isPlatform = studioAccess?.roles?.includes('platform_admin');
  const available = isPlatform ? AREAS : AREAS.filter(([id]) => studioAccess?.clinicalSourceAreas?.includes(id));
  const [area,setArea] = useState(available[0]?.[0] || 'hospital_journeys');
  const [allRows,setAllRows] = useState([]);
  const [search,setSearch] = useState('');
  const [page,setPage] = useState(0);
  const [queueFilter,setQueueFilter] = useState('outstanding');
  const [worklist,setWorklist] = useState(null);
  const [reviews,setReviews] = useState({});
  const [loading,setLoading] = useState(false);
  const [saving,setSaving] = useState('');
  const [notice,setNotice] = useState('');
  const [mismatch,setMismatch] = useState(false);
  const [drafts,setDrafts] = useState({});
  const [areaSummary,setAreaSummary] = useState(null);
  const [summaryStale,setSummaryStale] = useState(false);
  const [adminComment,setAdminComment] = useState('');
  const [working,setWorking] = useState('');
  const [dashboard,setDashboard] = useState(null);
  const [showDashboard,setShowDashboard] = useState(false);
  const refreshArea = async (selectedArea, filter = queueFilter, offset = page*SIZE) => {
    const next = await fetch('/api/studio-clinical-areas?area='+encodeURIComponent(selectedArea)+
      '&queueFilter='+encodeURIComponent(filter)+'&queueOffset='+offset,
      { credentials:'same-origin', cache:'no-store' });
    const payload = await next.json().catch(()=>({}));
    if(!next.ok) throw new Error(payload.error || 'Could not load clinical completion status');
    setAreaSummary(payload); setWorklist(payload.worklist || null); setSummaryStale(false);
    return payload;
  };
  useEffect(() => {
    if (!available.some(([id]) => id === area)) setArea(available[0]?.[0] || 'hospital_journeys');
  }, [studioAccess,area]);
  useEffect(() => {
    let cancelled=false;
    setLoading(true); setNotice(''); setReviews({}); setPage(0); setAreaSummary(null); setSummaryStale(false);
    if (!available.some(([id])=>id===area)) { setAllRows([]); setLoading(false); return undefined; }
    void refreshArea(area,queueFilter,0).catch(error=>{ if(!cancelled) setNotice(error.message); });
    loadAreaRows('en',area).then(rows=>{
      if (cancelled) return;
      const current = [...rows, ...clinicalOnlyRows].filter(row => row.contentArea === area && row.sourceText?.trim()).sort((a,b)=>a.id.localeCompare(b.id));
      setMismatch(current.some(row => sourceManifest.records[row.id]?.revision !== row.sourceRevision)
        || current.length !== Object.values(sourceManifest.records).filter(record=>record.contentArea===area).length);
      setAllRows(current); setLoading(false);
    }).catch(error=>{if (!cancelled){setNotice(error.message);setAllRows([]);setLoading(false);}});
    return ()=>{cancelled=true;};
  },[area,studioAccess]);
  const searching = Boolean(search.trim());
  const queueView = !searching && queueFilter !== 'all';
  const filtered = useMemo(()=>allRows.filter(row =>
    !search.trim() || (row.id+' '+row.sourceText).toLowerCase().includes(search.toLowerCase().trim())),[allRows,search]);
  const queueRows = useMemo(()=>{
    const byId = new Map(allRows.map(row=>[row.id,row]));
    return (worklist?.sourceIds || []).map(id=>byId.get(id)).filter(Boolean);
  },[allRows,worklist]);
  const maxPage=Math.max(0,Math.ceil((queueView ? worklist?.total || 0 : filtered.length)/SIZE)-1);
  const boundedPage=Math.min(page,maxPage);
  const visible = useMemo(()=>queueView ? queueRows :
    filtered.slice(boundedPage*SIZE,(boundedPage+1)*SIZE),
    [queueView,queueRows,filtered,boundedPage]);
  const changePage = next => {
    const target=Math.max(0,Math.min(maxPage,next));
    setPage(target);
    if (queueView) {
      setWorklist(null);
      void refreshArea(area,queueFilter,target*SIZE).catch(error=>setNotice(error.message));
    }
  };
  const changeQueue = filter => {
    setQueueFilter(filter);setSearch('');setPage(0);setWorklist(null);
    void refreshArea(area,filter,0).catch(error=>setNotice(error.message));
  };
  const ids=visible.map(row=>row.id).join(',');
  useEffect(()=>{
    let cancelled=false;
    if(!ids) return undefined;
    request('GET','?area='+encodeURIComponent(area)+'&ids='+encodeURIComponent(ids)).then(payload=>{
      if(cancelled)return;
      setMismatch(prev=>prev || payload.sourceDigest !== sourceManifest.digest);
      setReviews(curr=>({...curr,...payload.records}));
    }).catch(error=>{if(!cancelled)setNotice(error.message)});
    return()=>{cancelled=true};
  },[area,ids]);
  const update=(id,key,value)=>setDrafts(current=>({
    ...current,[id]:{...(current[id] || {}),[key]:value},
  }));
  const save=async row=>{
    const draft=drafts[row.id] || {};
    const decision=draft.decision || reviews[row.id]?.review?.decision || '';
    const comment=draft.comment ?? reviews[row.id]?.review?.comment ?? '';
    if(!decision){setNotice('Select a clinical decision first.');return;}
    setSaving(row.id);setNotice('');
    try{
      const data=await request('PUT','',{sourceId:row.id,sourceRevision:row.sourceRevision,decision,comment});
      setReviews(current=>({...current,[row.id]:{
        ...current[row.id],status:data.status,review:data.review,
      }}));
      setSummaryStale(true);
      setNotice('Clinical assessment recorded. Refresh area completion before submitting.');
    }catch(error){setNotice(error.message)}
    finally{setSaving('')}
  };
  const areaAction = async action => {
    setWorking(action);setNotice('');
    try {
      if(!areaSummary || summaryStale) throw new Error('Refresh the area completion report before taking this action');
      const res=await fetch('/api/studio-clinical-areas',{
        method:'POST',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({area,action,comment:adminComment,
          sourceDigest:areaSummary.report.sourceDigest,
          decisionDigest:areaSummary.report.decisionDigest}),
      });
      const payload=await res.json().catch(()=>({}));
      if(!res.ok) throw new Error(payload.error || 'Clinical area workflow failed');
      setNotice(action==='submit'?'Area submitted for independent Platform Admin sign-off.':
        action==='approve'?'Area approved at this source revision. This does not itself publish patient content.':
        'Area reopened for reviewer work.');
      setAdminComment('');
      await refreshArea(area);
    }catch(error){setNotice(error.message)}
    finally{setWorking('')}
  };
  const loadDashboard=async()=>{
    try {
      const res=await fetch('/api/studio-clinical-areas?format=dashboard',{
        credentials:'same-origin',cache:'no-store'});
      const data=await res.json().catch(()=>({}));
      if(!res.ok) throw new Error(data.error || 'Could not load review register');
      setDashboard(data.areas || []); setShowDashboard(true);
    }catch(error){setNotice(error.message)}
  };
  return <div className="app-shell">
    <header className="topbar"><div className="brand"><span className="brand-mark"><ShieldCheck/></span><div><strong><StudioBrand/></strong><small>National clinical source review</small></div></div></header>
    {notice && <div className="studio-notice" role="status">{notice}<button type="button" onClick={()=>setNotice('')} aria-label="Dismiss notification">×</button></div>}
    <div className="workspace">
      <aside className="catalogue">{workspaceSwitcher}<div className="catalogue-intro">
        <p className="eyebrow">Clinical assurance</p><h1>National patient content</h1>
        <p>Classify and assess every English source, including English-only Easy Read and printable text. Not every string is clinical; every exclusion needs a recorded rationale.</p>
        <p>Clinical decisions are stored with your verified login email, date and English source revision.</p>
        <p><strong>Important:</strong> recording a clinical assessment here does not itself approve a national patient release. Publication controls and second-person sign-off are separate.</p>
      </div>
      <div className="config-section"><label>Commissioned area
        <select value={area} onChange={event=>{setArea(event.target.value);setSearch('');setPage(0);setQueueFilter('outstanding');setWorklist(null);}}>
          {available.map(([id,label])=><option key={id} value={id}>{label}</option>)}
        </select></label></div></aside>
      <main className="review-workspace">
        {!available.length ? <section className="trust-config-panel"><h2>No national source-review commission</h2><p>Platform Admin must assign national clinical source areas in People & Access.</p></section> : <>
          <section className="trust-config-panel">
            <h2>Clinical review completion and independent sign-off</h2>
            <p>This summary includes every national patient source in this area, even if English-only for translation. Every item must be clinically accurate or classified nonclinical with a reason before submission. A completed review is not automatically a release approval.</p>
            <p><strong>Status:</strong> {areaSummary?.status?.replaceAll('_',' ') || 'Loading'} · <strong>Reviewed:</strong> {areaSummary?.report ? areaSummary.report.completed+' / '+areaSummary.report.total : '—'} · <strong>Outstanding:</strong> {areaSummary?.report?.outstanding ?? '—'}</p>
            {areaSummary?.report && <p>Missing: {areaSummary.report.blockers.unreviewed}; outdated: {areaSummary.report.blockers.source_changed}; clinical changes requested: {areaSummary.report.blockers.changes_requested}; needs context: {areaSummary.report.blockers.needs_context}; incomplete audit evidence: {areaSummary.report.blockers.missing_audit ?? 0}. Translation exclusions still requiring clinical triage: {areaSummary.report.translationExcluded}.</p>}
            {areaSummary?.submission?.submittedByEmail && <p>Submitted by {areaSummary.submission.submittedByEmail} on {new Date(areaSummary.submission.submittedAt).toLocaleString()}{areaSummary.submission.approvedAt ? '; approved '+new Date(areaSummary.submission.approvedAt).toLocaleString() : ''}.</p>}
            {summaryStale && <p role="status">You have saved new source decisions. Refresh to recalculate the complete area status.</p>}
            <div className="trust-actions">
              <button className="button secondary" disabled={Boolean(working)} onClick={()=>refreshArea(area).catch(error=>setNotice(error.message))}><RefreshCw size={16}/>Refresh completion</button>
              {!isPlatform && ['not_submitted','draft'].includes(areaSummary?.status)
                ? <button className="button primary" disabled={summaryStale || !areaSummary?.report?.complete || Boolean(working) || mismatch} onClick={()=>areaAction('submit')}>Submit completed area</button> : null}
              {isPlatform && areaSummary?.status==='submitted' && <button className="button primary" disabled={summaryStale || !areaSummary?.report?.complete || Boolean(working) || mismatch} onClick={()=>areaAction('approve')}>Approve clinical area</button>}
              {(areaSummary?.status==='submitted' && !isPlatform || isPlatform && ['submitted','approved','source_changed','review_changed'].includes(areaSummary?.status)) &&
                <button className="button secondary" disabled={Boolean(working)} onClick={()=>areaAction(isPlatform?'request_changes':'withdraw')}>Reopen area</button>}
              {isPlatform && <button className="button secondary" onClick={loadDashboard}>Platform Admin · all areas</button>}
            </div>
            {isPlatform && <label>Clinical sign-off feedback / request for changes<textarea rows={2} value={adminComment} onChange={e=>setAdminComment(e.target.value)} placeholder="Reason for reopening, if applicable"/></label>}
          </section>
          {showDashboard && isPlatform && dashboard && <section className="trust-config-panel">
            <h2>National clinical approval register</h2>
            <p>Source-versioned sign-off, separate from Trust approvals and professional translation. No automatic publication is authorised by this register.</p>
            <div style={{overflowX:'auto'}}><table><thead><tr><th>Content area</th><th>Approved status</th><th>Items</th><th>Reviewer email</th><th>Submitted</th><th>Approved</th></tr></thead><tbody>
              {dashboard.map(item=><tr key={item.area}><td>{AREAS.find(([id])=>id===item.area)?.[1] || item.area}</td><td>{item.status.replaceAll('_',' ')}</td><td>{item.completed}/{item.total}</td><td>{item.submittedByEmail||'—'}</td><td>{item.submittedAt ? new Date(item.submittedAt).toLocaleString() : '—'}</td><td>{item.approvedAt ? new Date(item.approvedAt).toLocaleString() : '—'}</td></tr>)}
            </tbody></table></div>
          </section>}
          <section className="trust-config-panel">
            <div className="trust-panel-heading"><div><h2>{AREAS.find(([id])=>id===area)?.[1]}</h2><p>{allRows.length} English patient-source entries for clinical relevance assessment. {visible.length} shown on this page.</p></div><ClipboardCheck/></div>
            {mismatch && <p role="alert">The reviewer source text differs from the authoritative server catalogue. Decisions are disabled until the current Studio build is deployed.</p>}
            <p>Translation exclusions do <strong>not</strong> exempt clinical review. Prior existing role and Trust review records are independent of these national source-level decisions.</p>
            <label>Review queue
              <select value={queueFilter} onChange={event=>changeQueue(event.target.value)}>
                <option value="outstanding">Outstanding decisions first</option>
                <option value="source_changed">Sources changed since review</option>
                <option value="changes_requested">Clinical changes requested</option>
                <option value="needs_context">Needs clinical context</option>
                <option value="not_reviewed">Never assessed</option>
                <option value="invalid_nonclinical">Missing exclusion rationale</option>
                <option value="missing_audit">Missing reviewer identity/date</option>
                <option value="completed">Decisions recorded (for spot-check)</option>
                <option value="all">All sources (alphabetical)</option>
              </select>
            </label>
            {queueView && <p role="status">{worklist ? worklist.total+' sources match this queue; no approval is inferred.': 'Loading review queue…'} Search below to inspect any source across the whole catalogue.</p>}
            <label>Find exact source or patient wording
              <input value={search} onChange={event=>{setSearch(event.target.value);setPage(0)}} placeholder="Search ID or English wording"/>
            </label>
            <div className="meta-row"><button type="button" className="button secondary" disabled={boundedPage===0 || (queueView && !worklist)} onClick={()=>changePage(boundedPage-1)}>Previous page</button>
              <span>Page {boundedPage+1} of {maxPage+1}</span>
              <button type="button" className="button secondary" disabled={boundedPage>=maxPage || (queueView && !worklist)} onClick={()=>changePage(boundedPage+1)}>Next page</button></div>
          </section>
          {loading ? <section className="trust-config-panel"><RefreshCw size={17}/>Loading English sources…</section> : visible.map(row=>{
            const state=reviews[row.id];
            const decision=drafts[row.id]?.decision || state?.review?.decision || '';
            const comment=drafts[row.id]?.comment ?? state?.review?.comment ?? '';
            return <section className="trust-config-panel" key={row.id}>
              <div className="trust-panel-heading"><div><p className="eyebrow">{row.namespace} · {row.path}</p>
                <h3>{state?.status?.replaceAll('_',' ') || 'Loading review status'}</h3></div>
                <span className="trust-workflow-status">{row.publicationTarget==='excluded' ? 'Translation-excluded; clinical triage required' : 'Patient content'}</span></div>
              <blockquote>{row.sourceText}</blockquote>
              <p><small>Version: {row.sourceRevision} · {row.id}</small></p>
              {state?.review && <p><strong>Previous reviewer:</strong> {state.review.reviewedByEmail || 'Not recorded'} · {state.review.reviewedAt ? new Date(state.review.reviewedAt).toLocaleString():'Date not recorded'}</p>}
              <label>Clinical assessment
                <select value={decision} onChange={event=>update(row.id,'decision',event.target.value)}>
                  <option value="">Choose an assessment</option>
                  {DECISIONS.map(([value,label])=><option key={value} value={value}>{label}</option>)}
                </select></label>
              <label>Rationale, clinical concern or context<textarea rows="2" value={comment} onChange={event=>update(row.id,'comment',event.target.value)} placeholder="Required for nonclinical classification, changes or missing context"/></label>
              <div className="trust-actions"><button type="button" className="button primary"
                disabled={Boolean(saving) || mismatch || ['submitted','approved','source_changed','review_changed'].includes(areaSummary?.status) || !state || !decision ||
                  (decision !== 'clinically_accurate' && comment.trim().length < 5)}
                onClick={()=>save(row)}><Save size={16}/>{saving===row.id?'Saving…':'Record clinical assessment'}</button></div>
            </section>;
          })}
        </>}
      </main>
    </div>
  </div>;
}
